import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { DESIGN_VARIANTS, OPERATOR } from "../../shared/constants";

// ゲスト営業用の公開ギャラリー(/g/:token)。認証不要。
// 運営が先に作った6案を「〇〇様のホームページデザイン案」として提示する。
// このページ以外にゲスト案件は露出しない(一般のLP/ナビからはリンクしない)。

interface GuestProposal {
  id: string;
  variant: string;
  image_url: string | null;
}

interface GuestGallery {
  token: string;
  office_name: string | null;
  profession: string;
  profession_label: string;
  status: string;
  generating: boolean;
  proposals_count: number;
  proposals: GuestProposal[];
}

const VARIANT_LABEL: Record<string, string> = Object.fromEntries(
  DESIGN_VARIANTS.map((v) => [v.value, v.label]),
);
const VARIANT_DESC: Record<string, string> = {
  standard: "信頼感を重視した定番の安心感あるデザイン",
  modern: "先進的で洗練されたテクノロジー感のあるデザイン",
  minimal: "余白を活かしたシンプルで美しいデザイン",
  natural: "やさしい色合いで親しみやすいナチュラルテイスト",
  premium: "高級感と重厚感を演出するプレミアムデザイン",
  creative: "個性と創造性を表現したクリエイティブなデザイン",
};

export function GuestGalleryPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<GuestGallery | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!token) return;
    let stopped = false;

    const poll = async () => {
      try {
        const res = await api.get<GuestGallery>(`/api/guest/${token}`);
        if (stopped) return;
        setData(res);
        setSelectedId((prev) => prev ?? res.proposals[0]?.id ?? null);
        if (!res.generating && res.proposals.length >= 6) {
          if (timer.current) clearInterval(timer.current);
        }
      } catch (err) {
        if (stopped) return;
        if (err instanceof ApiRequestError && err.status === 404) {
          setNotFound(true);
          if (timer.current) clearInterval(timer.current);
        }
      } finally {
        if (!stopped) setLoading(false);
      }
    };

    void poll();
    timer.current = setInterval(poll, 3000);
    return () => {
      stopped = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [token]);

  const proceed = () => {
    if (!token) return;
    const q = new URLSearchParams({ guest: token });
    if (selectedId) q.set("proposal", selectedId);
    navigate(`/register?${q.toString()}`);
  };

  if (loading) return <div className="page-center">読み込み中...</div>;

  if (notFound || !data) {
    return (
      <div className="guest-page">
        <div className="guest-card card center">
          <div className="brand center">{OPERATOR.serviceName}</div>
          <h1>ページが見つかりません</h1>
          <p className="muted">
            リンクが正しいかご確認ください。お心当たりのない場合は、お手数ですが担当者までご連絡ください。
          </p>
        </div>
      </div>
    );
  }

  const office = data.office_name || "貴事務所";
  const selected =
    data.proposals.find((p) => p.id === selectedId) ?? data.proposals[0] ?? null;

  return (
    <div className="guest-page">
      <header className="guest-hero">
        <div className="brand center">{OPERATOR.serviceName}</div>
        <span className="guest-eyebrow">{data.profession_label} ／ ホームページデザインご提案</span>
        <h1>{office} 様のホームページデザイン案</h1>
        <p className="muted">
          貴所のホームページを試作してみました。気に入るデザインがあれば、下からお選びください。
          お選びいただいたデザインで、そのまま制作を進められます。
        </p>
      </header>

      {data.generating ? (
        <div className="card generating guest-generating">
          <div className="progress-head">
            <div className="progress-percent">{data.proposals_count} / 6</div>
            <div className="muted small">
              ただいまデザイン案を生成しています（数分かかる場合があります）。
              このまま少々お待ちください。
            </div>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${Math.round((data.proposals_count / 6) * 100)}%` }}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="proposal-grid">
            {data.proposals.map((p, i) => (
              <button
                key={p.id}
                type="button"
                className={`proposal-card ${p.id === selectedId ? "on" : ""}`}
                onClick={() => setSelectedId(p.id)}
              >
                <div className="proposal-badge">{String(i + 1).padStart(2, "0")}</div>
                <span className="proposal-variant">
                  {VARIANT_LABEL[p.variant] ?? p.variant}
                </span>
                {p.image_url ? (
                  <img src={p.image_url} alt={VARIANT_LABEL[p.variant] ?? p.variant} />
                ) : (
                  <div className="proposal-noimg muted">画像準備中</div>
                )}
                <span className="proposal-select-state">
                  {p.id === selectedId ? "✓ 選択中" : "選択する"}
                </span>
              </button>
            ))}
          </div>

          {selected && (
            <div className="card preview-card">
              <h2>選択中のデザインプレビュー</h2>
              <div className="preview-body">
                {selected.image_url && (
                  <img className="preview-img" src={selected.image_url} alt="プレビュー" />
                )}
                <div className="preview-meta">
                  <h3>{VARIANT_LABEL[selected.variant] ?? selected.variant}</h3>
                  <p className="muted">{VARIANT_DESC[selected.variant]}</p>
                  <p className="muted small">
                    「このデザインで進める」を押すと、アカウント作成に進みます。
                    デザイン提案までは無料です。
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="guest-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={proceed}
              disabled={!selectedId}
            >
              このデザインで進める →
            </button>
          </div>
        </>
      )}

      <footer className="guest-footer muted small center">
        © {new Date().getFullYear()} {OPERATOR.companyName}
        <span className="guest-footer-sep">・</span>
        <Link to="/">{OPERATOR.serviceName} について</Link>
      </footer>
    </div>
  );
}
